import { useEffect } from 'react';
import { cn } from '../lib/utils';

const LABELS: Record<string, string> = {
  ctrl: 'Ctrl',
  control: 'Ctrl',
  alt: 'Alt',
  option: 'Alt',
  shift: 'Shift',
  meta: '⌘',
  cmd: '⌘',
  command: '⌘',
  arrowleft: '←',
  arrowright: '→',
  arrowup: '↑',
  arrowdown: '↓',
};

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent);

function label(part: string) {
  return LABELS[part] ?? (part.length === 1 ? part.toUpperCase() : part);
}

/**
 * Key hint. `char` is one key, including `ArrowRight`.
 * `keys` is a hotkeys-js chord such as `mod+i`.
 * hotkeys-js has no `mod` alias, so `mod` resolves to `command` on Apple platforms and `ctrl` elsewhere.
 */
export function Shortcut({
  char,
  keys,
  onKey,
  className,
}: {
  char?: string;
  keys?: string;
  onKey: () => void;
  className?: string;
}) {
  const combo = (keys ?? char ?? '').toLowerCase().replace(/\bmod\b/g, IS_MAC ? 'command' : 'ctrl');

  useEffect(() => {
    if (!combo) return;
    const down = (event: KeyboardEvent) => {
      event.preventDefault();
      onKey();
    };
    const loaded = import('hotkeys-js').then(({ default: hotkeys }) => {
      hotkeys(combo, down);
      return hotkeys;
    });
    return () => void loaded.then((hotkeys) => hotkeys.unbind(combo, down));
  }, [combo, onKey]);

  return (
    <span className={cn('inline-flex items-center gap-0.5', className)}>
      {combo
        .split('+')
        .filter(Boolean)
        .map((part) => (
          <kbd
            key={part}
            className="inline-flex h-5 min-w-5 items-center justify-center rounded border border-border-muted bg-background-elevated px-1 font-mono text-[10px] font-medium leading-none text-foreground"
          >
            {label(part)}
          </kbd>
        ))}
    </span>
  );
}
